// Chat-id allocation guards for a multi-build IndexedDB reality.
//
// Chat records key on `tenant:localId`, and every build mints new ids
// as max(visible ids) + 1. ALREADY-DEPLOYED builds compute "visible"
// with a hard `schemaVersion === 1` filter, so any record persisted at
// a newer schemaVersion is invisible to their counter: a stale-bundle
// tab cold-loading after chats migrate to v2 restarts near 1 and its
// first new chat put()s straight over a migrated record — silent data
// loss. We can't patch deployed code; we can only shape the data it
// reads. Two guards:
//
//   • New builds compute their counter from ALL real chat rows
//     regardless of schemaVersion (maxChatIdFromMetas — fence rows
//     excluded, see its comment), so this gap can't recur at the next
//     schema bump.
//   • The ID FENCE: a chatMeta-only row, pinned at schemaVersion 1 so
//     old builds' filters can see it, whose numeric local id sits
//     above every id in use. An old build's max-id computation then
//     lands ≥ the fence and it mints only unused ids. The fence has
//     no body record; new builds hide it from the tab list via the
//     `idFence` marker. If an old build adopts the fence id for a
//     real chat (its empty-tab reuse can), that id was unused — the
//     chat is legitimate, and the next new-build session re-fences
//     above it.
export function isIdFenceMeta(meta) {
    return !!meta?.idFence;
}

// Meta rows that should become visible chat tabs in a current build:
// schema this build understands, fence rows excluded. The version
// predicate is injected (ChatConsole passes chatSerializer's
// isSupportedSchemaVersion) so this module stays dependency-free.
export function visibleChatMetas(metas, isSupportedSchemaVersion) {
    return (Array.isArray(metas) ? metas : []).filter(
        (m) => m && isSupportedSchemaVersion(m.schemaVersion) && !isIdFenceMeta(m),
    );
}

// Highest numeric local id across every REAL chat row — all
// schemaVersions, fence rows excluded, non-numeric ids skipped. Floor
// of 1 mirrors the historical Math.max(1, ...) seed so next-id is
// always ≥ 2.
//
// Excluding the fence matters twice over: counting it would ratchet
// the fence +1 on every cold load (counter = fence + 1 → new fence =
// counter → …), an IDB delete+put per page load forever; and minting
// BELOW the fence is safe by construction — the fence has no body
// record, and if a new chat lands on its id the real chat's meta
// overwrites the marker and the next save re-fences above it. Old
// builds count the fence in their own max, so they mint strictly
// above it — the two id streams stay disjoint.
export function maxChatIdFromMetas(metas) {
    let max = 1;
    for (const m of Array.isArray(metas) ? metas : []) {
        if (isIdFenceMeta(m)) continue;
        const n = Number(m?.chatId);
        if (Number.isFinite(n) && n > max) max = n;
    }
    return max;
}

// Decide what ensureIdFence must write for a desired fence position:
//   { put: null }              — an existing fence already covers it
//   { put: id, deleteLocalIds } — write a fence at `id`, drop stale
//                                 lower fence rows
// Pure so the decision is unit-testable without IndexedDB.
export function resolveFenceAction(metas, targetFenceId) {
    const fences = (Array.isArray(metas) ? metas : []).filter(isIdFenceMeta);
    let currentMax = 0;
    for (const f of fences) {
        const n = Number(f?.chatId);
        if (Number.isFinite(n) && n > currentMax) currentMax = n;
    }
    if (currentMax >= targetFenceId) return { put: null, deleteLocalIds: [] };
    return {
        put: targetFenceId,
        deleteLocalIds: fences
            .map((f) => String(f?.chatId))
            .filter((id) => id !== String(targetFenceId)),
    };
}
